import React, { useEffect, useState } from "react";
import TenantUserService from "../../services/tenant/TenantUserService";
import TenantDataAreaService from "../../services/tenant/TenantDataAreaService";
import { faUsers, faKey } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CrudManager from "../../components/common/CrudManager";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useTranslation } from "react-i18next";
import fetchWithAuth from "../../utils/httpInterceptor";

const LANGUAGE_OPTIONS = ["es", "en", "ca", "eu", "gl", "pt", "pt-BR", "de", "fr", "it", "jp", "zh"];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200, 500];

const emptyUserPreferences = {
  defaultDataAreaId: "",
  defaultLanguage: "",
  defaultPageSize: 10,
};

const TenantUserFormTabs = ({
  tenantId,
  service,
  formData,
  onChange,
  isEditing,
  renderDefaultForm,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");
  const [availableDataAreas, setAvailableDataAreas] = useState([]);
  const [isLoadingDataAreas, setIsLoadingDataAreas] = useState(false);
  const [dataAreaError, setDataAreaError] = useState("");

  const selectedDataAreaIds = Array.isArray(formData.dataAreaIds)
    ? formData.dataAreaIds
    : [];
  const userPreferences = {
    ...emptyUserPreferences,
    ...(formData.userPreferences || {}),
  };

  useEffect(() => {
    let cancelled = false;

    const loadDataAreas = async () => {
      if (!tenantId) {
        setAvailableDataAreas([]);
        return;
      }
      setIsLoadingDataAreas(true);
      setDataAreaError("");
      try {
        const dataAreaService = new TenantDataAreaService(tenantId);
        const response = await dataAreaService.getAll(0, 500);
        const rows = response?.content || (Array.isArray(response) ? response : []);
        if (!cancelled) {
          setAvailableDataAreas(rows);
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableDataAreas([]);
          setDataAreaError(t("tenantUsers.dataAreaAccess.loadError"));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDataAreas(false);
        }
      }
    };

    loadDataAreas();

    return () => {
      cancelled = true;
    };
  }, [tenantId, t]);

  useEffect(() => {
    let cancelled = false;

    const loadUserSettings = async () => {
      if (!isEditing || !formData.id || !tenantId) {
        if (!Array.isArray(formData.dataAreaIds) || !formData.userPreferences) {
          onChange({
            ...formData,
            dataAreaIds: Array.isArray(formData.dataAreaIds) ? formData.dataAreaIds : [],
            userPreferences: formData.userPreferences || emptyUserPreferences,
          });
        }
        return;
      }

      setDataAreaError("");
      try {
        const [assignedDataAreas, preferences] = await Promise.all([
          service.listDataAreas(formData.id),
          service.getPreferences(formData.id),
        ]);
        if (!cancelled) {
          onChange({
            ...formData,
            dataAreaIds: Array.isArray(assignedDataAreas) ? assignedDataAreas : [],
            userPreferences: {
              defaultDataAreaId: preferences?.defaultDataAreaId || "",
              defaultLanguage: preferences?.defaultLanguage || "",
              defaultPageSize: Number(preferences?.defaultPageSize) || 10,
            },
          });
        }
      } catch (error) {
        if (!cancelled) {
          setDataAreaError(t("tenantUsers.preferences.loadError"));
        }
      }
    };

    loadUserSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, formData.id, tenantId, service, t]);

  const dataAreaKey = (dataArea) => dataArea.dataAreaId || dataArea.id;

  const setSelectedDataAreaIds = (nextIds) => {
    onChange({
      ...formData,
      dataAreaIds: Array.from(new Set(nextIds.map((value) => String(value).toUpperCase()))),
    });
  };

  const toggleDataArea = (dataAreaId) => {
    const normalized = String(dataAreaId).toUpperCase();
    if (selectedDataAreaIds.includes(normalized)) {
      setSelectedDataAreaIds(selectedDataAreaIds.filter((item) => item !== normalized));
      return;
    }
    setSelectedDataAreaIds([...selectedDataAreaIds, normalized]);
  };

  const selectAll = () => {
    setSelectedDataAreaIds(availableDataAreas.map(dataAreaKey).filter(Boolean));
  };

  const clearSelection = () => {
    setSelectedDataAreaIds([]);
  };

  const setUserPreference = (key, value) => {
    onChange({
      ...formData,
      userPreferences: {
        ...userPreferences,
        [key]: value,
      },
    });
  };

  return (
    <div className="tenant-user-form-tabs">
      <div className="crud-form-tabs-nav-wrap" aria-label={t("tenantUsers.tabs.title")}>
        <div className="crud-form-tabs-nav-scroll" role="tablist">
          <ul className="nav nav-tabs crud-form-tabs-nav">
            <li className={activeTab === "general" ? "active" : ""}>
              <button
                type="button"
                role="tab"
                className={`crud-form-tab-btn ${activeTab === "general" ? "is-active" : ""}`}
                onClick={() => setActiveTab("general")}
              >
                <i className="fa fa-user crud-form-tab-icon" aria-hidden="true"></i>
                <span>{t("tenantUsers.tabs.general")}</span>
              </button>
            </li>
            <li className={activeTab === "dataAreas" ? "active" : ""}>
              <button
                type="button"
                role="tab"
                className={`crud-form-tab-btn ${activeTab === "dataAreas" ? "is-active" : ""}`}
                onClick={() => setActiveTab("dataAreas")}
              >
                <i className="fa fa-building crud-form-tab-icon" aria-hidden="true"></i>
                <span>{t("tenantUsers.tabs.dataAreas")}</span>
              </button>
            </li>
            <li className={activeTab === "preferences" ? "active" : ""}>
              <button
                type="button"
                role="tab"
                className={`crud-form-tab-btn ${activeTab === "preferences" ? "is-active" : ""}`}
                onClick={() => setActiveTab("preferences")}
              >
                <i className="fa fa-sliders crud-form-tab-icon" aria-hidden="true"></i>
                <span>{t("tenantUsers.tabs.preferences")}</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="tab-content crud-form-tab-content">
        {activeTab === "general" ? (
          renderDefaultForm()
        ) : activeTab === "dataAreas" ? (
          <section className="tenant-user-dataarea-panel">
            <header className="tenant-user-dataarea-header">
              <div>
                <h4>{t("tenantUsers.dataAreaAccess.title")}</h4>
                <p>{t("tenantUsers.dataAreaAccess.description")}</p>
              </div>
              <div className="tenant-user-dataarea-actions">
                <button type="button" className="btn btn-secondary" onClick={selectAll}>
                  <i className="fa fa-check-square"></i>
                  <span>{t("tenantUsers.dataAreaAccess.selectAll")}</span>
                </button>
                <button type="button" className="btn btn-default" onClick={clearSelection}>
                  <i className="fa fa-eraser"></i>
                  <span>{t("tenantUsers.dataAreaAccess.clear")}</span>
                </button>
              </div>
            </header>

            {selectedDataAreaIds.length === 0 && (
              <div className="tenant-user-dataarea-inherit">
                <i className="fa fa-info-circle"></i>
                <span>{t("tenantUsers.dataAreaAccess.inheritAll")}</span>
              </div>
            )}

            {dataAreaError && (
              <div className="tenant-user-dataarea-error">{dataAreaError}</div>
            )}

            {isLoadingDataAreas ? (
              <div className="tenant-user-dataarea-empty">
                {t("tenantUsers.dataAreaAccess.loading")}
              </div>
            ) : availableDataAreas.length === 0 ? (
              <div className="tenant-user-dataarea-empty">
                {t("tenantUsers.dataAreaAccess.empty")}
              </div>
            ) : (
              <div className="tenant-user-dataarea-list">
                {availableDataAreas.map((dataArea) => {
                  const id = dataAreaKey(dataArea);
                  const normalized = String(id || "").toUpperCase();
                  const checked = selectedDataAreaIds.includes(normalized);
                  return (
                    <label className="tenant-user-dataarea-row" key={id}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDataArea(id)}
                      />
                      <span className="tenant-user-dataarea-code">{id}</span>
                      <span className="tenant-user-dataarea-name">{dataArea.name || ""}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </section>
        ) : (
          <section className="tenant-user-dataarea-panel">
            <header className="tenant-user-dataarea-header">
              <div>
                <h4>{t("tenantUsers.preferences.title")}</h4>
                <p>{t("tenantUsers.preferences.description")}</p>
              </div>
            </header>

            {dataAreaError && (
              <div className="tenant-user-dataarea-error">{dataAreaError}</div>
            )}

            <div className="tenant-user-preferences-grid">
              <label>
                <span>{t("tenantUsers.preferences.defaultDataAreaId")}</span>
                <select
                  className="form-control"
                  value={userPreferences.defaultDataAreaId || ""}
                  onChange={(event) => setUserPreference("defaultDataAreaId", event.target.value)}
                >
                  <option value="">{t("tenantUsers.preferences.noDefaultDataArea")}</option>
                  {availableDataAreas.map((dataArea) => {
                    const id = dataAreaKey(dataArea);
                    return (
                      <option key={id} value={id}>
                        {dataArea.name ? `${id} - ${dataArea.name}` : id}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label>
                <span>{t("tenantUsers.preferences.defaultLanguage")}</span>
                <select
                  className="form-control"
                  value={userPreferences.defaultLanguage || ""}
                  onChange={(event) => setUserPreference("defaultLanguage", event.target.value)}
                >
                  <option value="">{t("tenantUsers.preferences.noDefaultLanguage")}</option>
                  {LANGUAGE_OPTIONS.map((language) => (
                    <option key={language} value={language}>
                      {t(`languages.${language}`)}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>{t("tenantUsers.preferences.defaultPageSize")}</span>
                <select
                  className="form-control"
                  value={Number(userPreferences.defaultPageSize) || 10}
                  onChange={(event) => setUserPreference("defaultPageSize", Number(event.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const TenantUsers = () => {
  const { t } = useTranslation();
  const [flowBpmAvailable, setFlowBpmAvailable] = useState(false);
  const [resetMfaDialogOpen, setResetMfaDialogOpen] = useState(false);
  const [resetMfaRow, setResetMfaRow] = useState(null);
  const [resetMfaSubmitting, setResetMfaSubmitting] = useState(false);
  const [resetMfaError, setResetMfaError] = useState("");
  const [resetMfaSuccess, setResetMfaSuccess] = useState("");
  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const isSuperuser = userData?.superuser === true;

  // Use user.tenantId for non-superusers, or selectedTenant for superusers
  const selectedTenant = localStorage.getItem("selectedTenant");
  const tenantId = userData?.tenantId || selectedTenant;

  useEffect(() => {
    let cancelled = false;

    const loadFlowBpmStatus = async () => {
      if (!tenantId) {
        setFlowBpmAvailable(false);
        return;
      }
      try {
        const response = await fetchWithAuth("/api/flow-bpm/module/status", {
          method: "GET",
        });
        const status = await response.json();
        if (!cancelled) {
          setFlowBpmAvailable(status?.available === true);
        }
      } catch {
        if (!cancelled) {
          setFlowBpmAvailable(false);
        }
      }
    };

    loadFlowBpmStatus();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Define columns - flat structure
  // Note: 'id' column is required by CrudManager - it maps to 'userId' from the backend
  const columns = [
    {
      key: "id",
      // Hidden column - used internally for CRUD operations
      showInGrid: false,
      showInForm: false,
    },
    {
      key: "userId",
      label: "tenantUsers.userId",
      showInGrid: false,
    },
    {
      key: "email",
      label: "tenantUsers.email",
      sortable: true,
      showInFilter: true,
      type: "text",
      // Form:
      showInForm: true,
      placeholder: "tenantUsers.emailPlaceholder",
      required: true,
    },
    {
      key: "subject",
      label: "tenantUsers.subject",
      sortable: true,
      showInFilter: true,
      type: "text",
      // Form:
      showInForm: true,
      placeholder: "tenantUsers.subjectPlaceholder",
      required: true,
    },
    {
      key: "displayName",
      label: "tenantUsers.displayName",
      sortable: true,
      showInFilter: true,
      type: "text",
      // Form:
      showInForm: true,
      placeholder: "tenantUsers.displayNamePlaceholder",
      required: true,
    },
    {
      key: "authProvider",
      label: "tenantUsers.authProvider",
      sortable: true,
      showInFilter: true,
      type: "select",
      // Form:
      showInForm: true,
      options: [
        { value: "external", label: "external" },
        { value: "local", label: "local" },
      ],
    },
    {
      key: "password",
      label: "tenantUsers.password",
      // Form:
      showInForm: true,
      showInGrid: false,
      type: "password",
      placeholder: "tenantUsers.passwordPlaceholder",
    },
    {
      key: "role",
      label: "tenantUsers.role",
      sortable: true,
      showInFilter: true,
      type: "select",
      // Form:
      showInForm: true,
      options: [
        { value: "USER", label: "USER" },
        { value: "OWNER", label: "OWNER" },
        { value: "ADMIN", label: "ADMIN" },
      ],
    },
    ...(flowBpmAvailable
      ? [
          {
            key: "bpmUserEnabled",
            label: "tenantUsers.bpmUserEnabled",
            sortable: true,
            showInFilter: true,
            type: "checkbox",
            showInForm: true,
            className: "tenant-user-bpm-field",
          },
        ]
      : []),
  ];

  // Entity name based on user role: 'tenantUsers' for superusers, 'users' for regular users
  const entityName = isSuperuser ? "tenantUsers" : "users";

  // Create service instance (tenantId may be null while a superuser hasn't picked one yet -
  // the CrudManager below shows noDataMessage in that case, see the `!tenantId` branch).
  const service = new TenantUserService(tenantId, { flowBpmAvailable });

  const openResetMfa = (row) => {
    setResetMfaRow(row);
    setResetMfaError("");
    setResetMfaSuccess("");
    setResetMfaDialogOpen(true);
  };

  const closeResetMfa = () => {
    setResetMfaDialogOpen(false);
    setResetMfaRow(null);
    setResetMfaError("");
    setResetMfaSuccess("");
  };

  const confirmResetMfa = async () => {
    if (!resetMfaRow) return;
    setResetMfaSubmitting(true);
    try {
      await service.resetMfa(resetMfaRow.id);
      setResetMfaError("");
      setResetMfaSuccess(t("tenantUsers.mfaReset.success"));
      setResetMfaRow(null);
    } catch (e) {
      setResetMfaSuccess("");
      setResetMfaError(t("tenantUsers.mfaReset.error"));
    } finally {
      setResetMfaSubmitting(false);
    }
  };

  const renderUserRowActions = (row) => (
    <FontAwesomeIcon
      icon={faKey}
      className="action-icon action-edit"
      title={t("tenantUsers.mfaReset.tooltip")}
      aria-label={t("tenantUsers.mfaReset.tooltip")}
      onClick={() => openResetMfa(row)}
    />
  );

  const resetMfaDialog = (
    <ConfirmDialog
      isOpen={resetMfaDialogOpen}
      onClose={closeResetMfa}
      onConfirm={confirmResetMfa}
      title={t("tenantUsers.mfaReset.confirmTitle")}
      message={t("tenantUsers.mfaReset.confirmMessage")}
      confirmText={t("tenantUsers.mfaReset.confirmButton")}
      cancelText={t("common.cancel")}
      icon={faKey}
      type="danger"
      successMessage={resetMfaSuccess}
      errorMessage={resetMfaError}
      onSuccessClose={closeResetMfa}
      onErrorClose={() => setResetMfaError("")}
      isProcessing={resetMfaSubmitting}
    />
  );

  // If no tenantId, show message (only for superusers)
  if (!tenantId) {
    if (isSuperuser) {
      return (
        <>
          <CrudManager
            service={service}
            columns={columns}
            entityName={entityName}
            permissionPrefix="system.users"
            icon={faUsers}
            formLayout="modal"
            modalClassName="tenant-user-modal"
            renderForm={(props) => (
              <TenantUserFormTabs
                {...props}
                tenantId={tenantId}
                service={service}
              />
            )}
            renderRowActions={renderUserRowActions}
            noDataMessage="tenantUsers.errors.noTenant"
          />
          {resetMfaDialog}
        </>
      );
    }
    return null;
  }

  return (
    <>
      <CrudManager
        service={service}
        columns={columns}
        entityName={entityName}
        permissionPrefix="system.users"
        icon={faUsers}
        formLayout="modal"
        modalClassName="tenant-user-modal"
        renderForm={(props) => (
          <TenantUserFormTabs
            {...props}
            tenantId={tenantId}
            service={service}
          />
        )}
        renderRowActions={renderUserRowActions}
      />
      {resetMfaDialog}
    </>
  );
};

export default TenantUsers;
